// Modal listy pozycji kalkulacji (krok 2 flowu "Dodaj kalkulacje").
// Wyświetla tabelę pozycji, umożliwia dodanie nowej pozycji oraz usunięcie istniejącej.

import React, { type FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, CircularProgress, IconButton, TextField } from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/EditOutlined';
import { useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';

import { WiseModal } from 'components/wise';
import {
  useGetSalesCrmOfferCalculationPositions,
  useDeleteSalesCrmOfferCalculationPosition,
  useDeleteSalesCrmOfferCalculation,
  usePutSalesCrmOfferCalculation,
  getSalesCrmOfferCalculationPositionsQueryKey,
  type ISalesCrmOfferCalculationPosition
} from 'api';
import CalculationPositionCostCell from './CalculationPositionCostCell';

import styles from 'theme/components/containers/TraderClientOfferProductsContainer/CalculationPositionsModal.module.scss';

interface ICalculationPositionsModalProps {
  open: boolean;
  onClose: () => void;
  onAddPosition: () => void;
  /** Edycja konkretnej pozycji kalkulacji. */
  onEditPosition: (position: ISalesCrmOfferCalculationPosition) => void;
  /** Callback po zapisie metadanych (nazwa/opis) — pozwala rodzicowi zaktualizowac state. */
  onMetadataSaved?: (next: { name: string; description: string }) => void;
  /**
   * Callback po dowolnej zmianie pozycji kalkulacji wykonanej z poziomu tego modala
   * (obecnie usuniecie pozycji). Rodzic powinien zinwalidowac dane oferty, bo ceny
   * pozycji oferty zaleza od pozycji kalkulacji.
   */
  onPositionsChanged?: () => void;
  /** Callback po usunieciu calej kalkulacji — rodzic zamyka modal i odswieza dane oferty. */
  onCalculationDeleted?: () => void;
  clientId: number;
  offerId: string;
  calculationId: number;
  calculationName: string;
  calculationDescription?: string;
}

const formatNumber = (value: number | null | undefined): string => {
  if (value == null || Number.isNaN(value)) return '-';
  return new Intl.NumberFormat('pl-PL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

const CalculationPositionsModal: FC<ICalculationPositionsModalProps> = ({
  open,
  onClose,
  onAddPosition,
  onEditPosition,
  onMetadataSaved,
  onPositionsChanged,
  onCalculationDeleted,
  clientId,
  offerId,
  calculationId,
  calculationName,
  calculationDescription
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { data, isLoading, isError } = useGetSalesCrmOfferCalculationPositions(
    clientId,
    offerId,
    calculationId,
    { enabled: open && calculationId > 0 }
  );

  const items: ISalesCrmOfferCalculationPosition[] = data?.items ?? [];

  const { mutate: deletePosition, isLoading: isDeleting } =
    useDeleteSalesCrmOfferCalculationPosition(clientId, offerId, calculationId, {
      onSuccess: () => {
        queryClient.invalidateQueries(
          getSalesCrmOfferCalculationPositionsQueryKey(calculationId)
        );
        // Usuniecie pozycji kalkulacji zmienia cene bazowa pozycji oferty
        // (backend rekalkuluje przez RecalculateOfferVersionViaCartService) —
        // rodzic odswieza dane oferty zeby UI nie pokazywal starej ceny.
        onPositionsChanged?.();
      }
    });

  const handleDelete = (positionId: number) => {
    deletePosition(positionId);
  };

  // ====== Edycja metadanych kalkulacji (nazwa + opis) ======
  const [nameDraft, setNameDraft] = useState<string>(calculationName);
  const [descriptionDraft, setDescriptionDraft] = useState<string>(
    calculationDescription ?? ''
  );

  // Reset draftow przy otwarciu modala lub zmianie kalkulacji.
  useEffect(() => {
    if (open) {
      setNameDraft(calculationName);
      setDescriptionDraft(calculationDescription ?? '');
    }
  }, [open, calculationId, calculationName, calculationDescription]);

  const { mutate: updateCalculation, isLoading: isSavingMeta } =
    usePutSalesCrmOfferCalculation(clientId, offerId, calculationId, {
      onSuccess: () => {
        enqueueSnackbar(t('salesCrm.calculation.metadata.saved'), { variant: 'success' });
        onMetadataSaved?.({
          name: nameDraft.trim(),
          description: descriptionDraft
        });
      },
      onError: () => {
        enqueueSnackbar(t('salesCrm.calculation.metadata.saveError'), { variant: 'error' });
      }
    });

  const isMetaDirty =
    nameDraft.trim() !== calculationName.trim() ||
    descriptionDraft !== (calculationDescription ?? '');

  const handleSaveMetadata = () => {
    const trimmedName = nameDraft.trim();
    if (trimmedName.length === 0) {
      enqueueSnackbar(t('salesCrm.calculation.metadata.nameRequired'), {
        variant: 'warning'
      });
      return;
    }
    updateCalculation({
      name: trimmedName,
      description: descriptionDraft
    });
  };

  // ====== Usuniecie calej kalkulacji ======
  const { mutate: deleteCalculation, isLoading: isDeletingCalc } =
    useDeleteSalesCrmOfferCalculation(clientId, offerId, {
      onSuccess: () => {
        enqueueSnackbar(t('salesCrm.calculation.delete.success'), { variant: 'success' });
        onCalculationDeleted?.();
      },
      onError: () => {
        enqueueSnackbar(t('salesCrm.calculation.delete.error'), { variant: 'error' });
      }
    });

  const handleDeleteCalculation = () => {
    if (calculationId <= 0 || isDeletingCalc) return;
    const confirmed = window.confirm(
      t('salesCrm.calculation.delete.confirm', { name: calculationName })
    );
    if (!confirmed) return;
    deleteCalculation(calculationId);
  };

  const priceTypeLabel = (priceType: 'TOTAL' | 'UNIT') =>
    priceType === 'TOTAL'
      ? t('salesCrm.calculation.priceType.total')
      : t('salesCrm.calculation.priceType.unit');

  return (
    <WiseModal
      open={open}
      onClose={onClose}
      title={t('salesCrm.calculation.positions.title', { name: calculationName })}
      maxWidth={920}>
      <div className={styles.body}>
        <div className={styles.metaSection}>
          <div className={styles.metaField}>
            <label className={styles.metaLabel} htmlFor="calc-meta-name">
              {t('salesCrm.calculation.metadata.name')}
            </label>
            <TextField
              id="calc-meta-name"
              size="small"
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              fullWidth
            />
          </div>
          <div className={styles.metaField}>
            <label className={styles.metaLabel} htmlFor="calc-meta-description">
              {t('salesCrm.calculation.metadata.description')}
            </label>
            <TextField
              id="calc-meta-description"
              size="small"
              value={descriptionDraft}
              onChange={(e) => setDescriptionDraft(e.target.value)}
              multiline
              minRows={2}
              maxRows={6}
              fullWidth
            />
          </div>
          <div className={styles.metaActions}>
            <Button
              onClick={handleDeleteCalculation}
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteIcon fontSize="small" />}
              disabled={isDeletingCalc}>
              {isDeletingCalc
                ? t('salesCrm.calculation.delete.deleting')
                : t('salesCrm.calculation.delete.cta')}
            </Button>
            <Button
              onClick={handleSaveMetadata}
              variant="outlined"
              size="small"
              disabled={!isMetaDirty || isSavingMeta}>
              {isSavingMeta
                ? t('salesCrm.calculation.metadata.saving')
                : t('salesCrm.calculation.metadata.save')}
            </Button>
          </div>
        </div>

        <div className={styles.toolbar}>
          <Button
            onClick={onAddPosition}
            variant="contained"
            className={styles.primaryButton}>
            {t('salesCrm.calculation.positions.addPosition')}
          </Button>
        </div>

        {isLoading && (
          <div className={styles.loading}>
            <CircularProgress size={24} />
          </div>
        )}

        {!isLoading && isError && (
          <div className={styles.error}>{t('salesCrm.calculation.positions.loadError')}</div>
        )}

        {!isLoading && !isError && items.length === 0 && (
          <div className={styles.empty}>{t('salesCrm.calculation.positions.empty')}</div>
        )}

        {!isLoading && !isError && items.length > 0 && (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.th}>{t('salesCrm.calculation.positions.column.name')}</th>
                  <th className={styles.th}>{t('salesCrm.calculation.positions.column.priceType')}</th>
                  <th className={`${styles.th} ${styles.numeric}`}>
                    {t('salesCrm.calculation.positions.column.costNet')}
                  </th>
                  <th className={`${styles.th} ${styles.numeric}`}>
                    {t('salesCrm.calculation.positions.column.priceNet')}
                  </th>
                  <th className={`${styles.th} ${styles.numeric}`}>
                    {t('salesCrm.calculation.positions.column.taxPercent')}
                  </th>
                  <th className={`${styles.th} ${styles.numeric}`}>
                    {t('salesCrm.calculation.positions.column.priceGross')}
                  </th>
                  <th
                    className={`${styles.th} ${styles.actionsCell}`}
                    aria-label={t('salesCrm.calculation.positions.column.actions')}
                  />
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id} className={styles.tr}>
                    <td className={styles.td}>{row.name}</td>
                    <td className={styles.td}>
                      <span className={styles.priceTypeBadge}>
                        {priceTypeLabel(row.priceType)}
                      </span>
                    </td>
                    <td className={`${styles.td} ${styles.numeric}`}>
                      <CalculationPositionCostCell
                        clientId={clientId}
                        productId={row.productId}
                        costNet={row.costNet ?? null}
                      />
                    </td>
                    <td className={`${styles.td} ${styles.numeric}`}>
                      {formatNumber(row.priceNet)}
                    </td>
                    <td className={`${styles.td} ${styles.numeric}`}>
                      {formatNumber(row.taxPercent)}
                    </td>
                    <td className={`${styles.td} ${styles.numeric}`}>
                      {formatNumber(row.priceGross)}
                    </td>
                    <td className={`${styles.td} ${styles.actionsCell}`}>
                      <IconButton
                        size="small"
                        aria-label={t('salesCrm.calculation.positions.edit')}
                        onClick={() => onEditPosition(row)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label={t('salesCrm.calculation.positions.delete')}
                        onClick={() => handleDelete(row.id)}
                        disabled={isDeleting}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className={styles.footer}>
          <Button onClick={onClose} variant="text">
            {t('common.close')}
          </Button>
        </div>
      </div>
    </WiseModal>
  );
};

export default CalculationPositionsModal;
